import React, { useEffect, useState } from 'react'

/* eslint-disable max-len */
import axios, { AxiosResponse } from 'axios'
import { useAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import qs from 'qs'
import * as randomString from 'randomstring'
import { encode as base64encode } from 'base64-arraybuffer'
import { hasAccessToCase } from '../api/salary'
import {
    profileAtom,
    getProfile,
    getProfileByIdent,
} from '../api/profile/profile'
import { Dialog } from '../components/ui/Dialog/Dialog'
import { Typography } from '../components/ui/Typography/Typography'
import { Button } from '../components/ui/Button/Button'
import { Foa, LockFill } from '../assets/icons'
import { Profile } from '../models/Profile'
import { Imitator } from '../components/modules/Imitator'
import {
    isAuthenticatedAtom,
    chooseImitatorAtom,
    authRoles,
    canAccessLoenindplaceringer,
} from '../store/auth'
import { L_HTML } from '../components/providers/Lokalise'
import { Spinner } from '../components/ui/Loaders/Spinner'
import { userIsActiveInApp } from '../api/misc'
import { colors } from '../theme/palette'

const PERIOD_CHECK_INTERVAL = 60 * 1000 * 5

// Where to send the user after the ADFS login round trip. Only registered
// redirect URIs are accepted by ADFS, so we always use the root URI and
// restore the original path (incl. query string, e.g. /sag?sagsId=<id>) here.
const PRE_LOGIN_RETURN_PATH_KEY = 'pre_login_return_path'

const oidcSettings = {
    authority: process.env.REACT_APP_AUTH_AUTHORITY,
    client_id: process.env.REACT_APP_AUTH_CLIENT_ID,
    // Root URI with trailing slash, matching what is registered in ADFS.
    redirect_uri: `${window.location.origin}/`,
    response_type: process.env.REACT_APP_AUTH_RESPONSE_TYPE,
    scope: process.env.REACT_APP_AUTH_REDIRECT_URI_SCOPE,
}

// Disabled for now.
// process.env.REACT_APP_AUTH_SETORIGIN
const setOriginUrl = undefined

export interface IUserData {
    access_token: string
    expires_in: number
    id_token: string
    refresh_token: string
    refresh_token_expires_in: string
    resource: string
    scope: string
    token_type: string
}

const Auth0: React.FC = ({ children }) => {
    const navigate = useNavigate()
    const [isAuthenticated, setIsAuthenticated] = useAtom(isAuthenticatedAtom)
    const [, setProfile] = useAtom(profileAtom)
    const [chooseImitator, setChooseImitator] = useAtom(chooseImitatorAtom)
    const [loading, _setLoading] = useState(true)
    const [, _setRoles] = useAtom(authRoles)
    const [, _setCanAccessLoenindplaceringer] = useAtom(
        canAccessLoenindplaceringer
    )
    const [periodicCheckInitiated, setPeriodicCheckInitiated] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | undefined>(
        undefined
    )

    const intiatePeriodicCheck = (): void => {
        if (!periodicCheckInitiated) {
            setPeriodicCheckInitiated(true)
            userIsActiveInApp()
            setInterval(() => {
                getProfile()
            }, PERIOD_CHECK_INTERVAL)
        }
    }

    const validateImitator = async (
        profileData: AxiosResponse
    ): Promise<void> => {
        const authP = {
            data: profileData.data,
            isImitator: true,
            apiProfile: undefined,
        }

        const imitatorInStorage = localStorage.getItem('imitator')

        if (imitatorInStorage) {
            const member = JSON.parse(imitatorInStorage)
            const response = await getProfileByIdent(member.memberId)
            const profile = Profile.build(response.data)
            const hasAccessToCaseResponse = await hasAccessToCase()
            _setCanAccessLoenindplaceringer(hasAccessToCaseResponse.data)
            authP.apiProfile = profile
            _setRoles([member.role])
            setIsAuthenticated(true)
            setChooseImitator(false)
            _setLoading(false)
            setProfile(authP)
        } else {
            setProfile(authP)
            localStorage.setItem('profile_data', JSON.stringify(authP))
            setIsAuthenticated(false)
            setChooseImitator(true)
            _setLoading(false)
        }
    }

    const setAlreadyAuthenticated = async (profileData: any): Promise<void> => {
        const response = await getProfileByIdent(
            profileData.data.BrugerMedlemsident
        )

        if (response && response.error) {
            localStorage.setItem('profile_data', null)
            if (response.error && response.error.Message) {
                setErrorMessage(response.error.Message)
            }
        } else {
            setErrorMessage(undefined)
            const hasAccessToCaseResponse = await hasAccessToCase()
            const activeProfile = Profile.build(response.data)
            const authP = {
                data: profileData.data,
                isImitator: false,
                apiProfile: activeProfile,
            }

            _setRoles(profileData.data.RolleListe)
            _setCanAccessLoenindplaceringer(hasAccessToCaseResponse.data)
            localStorage.setItem('profile_data', JSON.stringify(authP))

            setProfile(authP)

            if (profileData.data) {
                setIsAuthenticated(true)
            }
        }

        _setLoading(false)
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function parseJwt(token) {
        const base64Url = token.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(
            window
                .atob(base64)
                .split('')
                .map(function (c: any) {
                    return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`
                })
                .join('')
        )

        return JSON.parse(jsonPayload)
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    async function getUserInfo(accessToken, id_token) {
        try {
            const response = await axios.get(
                'https://fs10.foa.dk/adfs/userinfo',
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    params: {
                        id_token,
                        scope: oidcSettings.scope,
                    },
                }
            )
            return response.data
        } catch (error) {
            console.error(
                'Error fetching user info:',
                error.response ? error.response.data : error.message
            )
            throw error // Re-throw the error to be handled by the calling function
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const getToken = async (code: string) => {
        const code_verifier = localStorage.getItem('code_verifier')
        axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
        const response = await axios.post(
            `${oidcSettings.authority}/oauth2/token`,
            qs.stringify({
                grant_type: 'authorization_code',
                redirect_uri: oidcSettings.redirect_uri,
                client_id: oidcSettings.client_id,
                code_verifier,
                code,
            })
        )

        const user: IUserData = response.data
        localStorage.setItem('user_data', JSON.stringify(user))
        const profileData = await getProfile()
        const roleList = profileData.data.RolleListe

        if (
            roleList &&
            (roleList.indexOf('Supporter') !== -1 ||
                roleList.indexOf('Imitator') !== -1)
        ) {
            validateImitator(profileData)
            intiatePeriodicCheck()
        } else {
            setAlreadyAuthenticated(profileData)
            intiatePeriodicCheck()
        }

        const returnPath = sessionStorage.getItem(PRE_LOGIN_RETURN_PATH_KEY)
        sessionStorage.removeItem(PRE_LOGIN_RETURN_PATH_KEY)

        if (returnPath) {
            navigate(returnPath, { replace: true })
        } else {
            window.history.replaceState(
                {},
                document.title,
                window.location.pathname
            )
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const checkUser = async (code: string | undefined) => {
        _setLoading(true)
        const userData = localStorage.getItem('user_data')

        if (userData && !code) {
            const profileData = await getProfile()
            const roleList = profileData.data.RolleListe
            const user = JSON.parse(userData)
            const jwt = parseJwt(user.id_token)

            if (
                roleList &&
                (roleList.indexOf('Supporter') !== -1 ||
                    roleList.indexOf('Imitator') !== -1)
            ) {
                validateImitator(profileData)
                intiatePeriodicCheck()
            } else {
                setAlreadyAuthenticated(profileData)
                intiatePeriodicCheck()
            }
        } else if (code && code !== '') {
            getToken(code)
        } else if (!userData && !code) {
            setIsAuthenticated(false)
            _setLoading(false)
        }
    }

    useEffect(() => {
        const queryString = window.location.search
        const params = new URLSearchParams(queryString)
        const code = params.get('code')
        if (code) {
            sessionStorage.setItem('testing', JSON.stringify(window.location))
        }

        _setLoading(true)

        checkUser(code)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const signIn = async () => {
        // if (setOriginUrl && setOriginUrl !== '') {
        //     await axios.get(setOriginUrl)
        // }
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        async function generateCodeChallenge(codeVerifier) {
            const encoder = new TextEncoder()
            const data = encoder.encode(codeVerifier)
            const digest = await window.crypto.subtle.digest('SHA-256', data)
            const base64Digest = base64encode(digest)

            // you can extract this replacing code to a function
            return base64Digest
                .replace(/\+/g, '-')
                .replace(/\//g, '_')
                .replace(/=/g, '')
        }

        const code_verifier = randomString.generate(128)

        generateCodeChallenge(code_verifier).then((challenge) => {
            localStorage.setItem('code_verifier', code_verifier)
            sessionStorage.setItem(
                PRE_LOGIN_RETURN_PATH_KEY,
                window.location.pathname + window.location.search
            )
            const qParams = [
                `redirect_uri=${oidcSettings.redirect_uri}`,
                `scope=${oidcSettings.scope}`,
                `response_type=${oidcSettings.response_type}`,
                `client_id=${oidcSettings.client_id}`,
                `code_challenge_method=S256`,
                `code_challenge=${challenge}`,
                // `${
                //     process.env.NODE_ENV !== 'production' ? 'prompt=login' : ''
                // }`,
                'max_age=1200',
            ].join('&')

            window.location.href = `${oidcSettings.authority}/oauth2/authorize?${qParams}`
        })
    }

    const logout = (): void => {
        const localUser = localStorage.getItem('user_data')
        const user: IUserData = JSON.parse(localUser)
        const token = user.id_token
        const redirect_uri = process.env.REACT_APP_AUTH_REDIRECT_URI

        const translations = localStorage.getItem('translations')
        localStorage.clear()
        localStorage.setItem('translations', translations)

        const qParams = [
            `id_token_hint=${token}`,
            `post_logout_redirect_uri=${redirect_uri}`,
        ].join('&')

        const logoutURL = `https://fs10.foa.dk/adfs/oauth2/logout?${qParams}`

        window.location.href = logoutURL

        axios.get(logoutURL).then(() => {
            window.history.replaceState(
                {},
                document.title,
                window.location.pathname
            )
        })
    }

    if (loading) {
        return (
            <div style={{ marginTop: '100px' }}>
                <Spinner />
            </div>
        )
    }

    if (!isAuthenticated && chooseImitator) {
        return (
            <>
                <Imitator />
                {setOriginUrl ? (
                    <img
                        src={setOriginUrl}
                        alt="cors"
                        style={{
                            height: '1px',
                            width: '1px',
                            position: 'absolute',
                            left: '-999999px',
                        }}
                    />
                ) : null}
            </>
        )
    }

    if (isAuthenticated) {
        return (
            <div>
                {setOriginUrl ? (
                    <img
                        src={setOriginUrl}
                        alt="cors"
                        style={{
                            height: '1px',
                            width: '1px',
                            position: 'absolute',
                            left: '-999999px',
                        }}
                    />
                ) : null}
                {children}
            </div>
        )
    }

    return (
        <>
            <div
                style={{
                    width: '100dvw',
                    height: '100dvh',
                    backgroundColor: colors.green,
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        top: '3vw',
                        left: '3vw',
                        opacity: 0,
                        animation: 'fadeInLogo .8s ease-in-out forwards',
                    }}
                >
                    <Foa width="100px" />
                </div>
                <div
                    style={{
                        fontSize: 'clamp(4rem, 12vw, 12rem)',
                        fontWeight: 'bold',
                        color: 'rgba(255, 255, 255, 0.1)',
                        userSelect: 'none',
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%) rotate(-15deg)',
                        opacity: 0,
                        animation: 'fadeInText .8s ease-in-out forwards',
                    }}
                >
                    mine medlemmer
                </div>
                <style>
                    {`
                        @keyframes fadeInLogo {
                            from {
                                opacity: 0;
                            }
                            to {
                                opacity: 0.08;
                            }
                        }
                        @keyframes fadeInText {
                            from {
                                opacity: 0;
                            }
                            to {
                                opacity: 0.2;
                            }
                        }
                    `}
                </style>
            </div>
            <Dialog
                trigger={null}
                defaultOpen
                hideClose
                icon={<LockFill width="46px" />}
                hideOverlay
            >
                <>
                    {setOriginUrl ? (
                        <img
                            src={setOriginUrl}
                            alt="cors"
                            style={{
                                height: '1px',
                                width: '1px',
                                position: 'absolute',
                                left: '-999999px',
                            }}
                        />
                    ) : null}
                    {!errorMessage ? (
                        <>
                            <Typography
                                style={{ marginBottom: '30px' }}
                                size="L"
                            >
                                {/* {L_HTML('LOGIN_POPUP_OVERSKRIFT')} */}
                                Log på
                            </Typography>
                            <Typography
                                tag="p"
                                size="S"
                                style={{ marginBottom: '30px' }}
                                align="center"
                            >
                                {/* {L_HTML('LOGIN_POPUP_TEXT')} */}
                                Du skal logge på for at få adgang til Mine
                                medlemmer
                            </Typography>
                            <Button size="xlarge" onClick={signIn}>
                                OK
                            </Button>
                        </>
                    ) : (
                        <>
                            <Typography
                                style={{ marginBottom: '30px' }}
                                size="L"
                            >
                                {L_HTML('LOGIN_POPUP_OVERSKRIFT')}
                            </Typography>
                            <Typography
                                tag="p"
                                size="S"
                                style={{ marginBottom: '30px' }}
                                align="center"
                            >
                                {errorMessage ? (
                                    <Typography
                                        tag="p"
                                        size="S"
                                        style={{ marginBottom: '30px' }}
                                        align="center"
                                    >
                                        {errorMessage}
                                    </Typography>
                                ) : null}
                            </Typography>
                            <Button size="xlarge" onClick={logout}>
                                Log ud
                            </Button>
                        </>
                    )}
                </>
            </Dialog>
        </>
    )
}
export default Auth0
